import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { BiTrash } from "react-icons/bi";
import { RiShoppingBag3Line } from "react-icons/ri";
import {
  toRemoveProduct,
  toRemoveService,
  toUpdateQuantity,
} from "../../../apis/slices/globalSlice";
import { useTranslation } from "react-i18next";
import { hasEffectiveDiscount } from "../../../utils/effectivePrice";
import ServiceThumbnail from "../../../Components/shop/ServiceThumbnail";
import { isValidServiceImage } from "../../../utils/serviceImage";
import { Spin, Alert, Button } from "antd";
import { requestCartSync } from "../../../apis/actions/cart.actions";
import { formatTintSummary } from "../../../utils/windowTinting";

export interface OrderLinesProps {
  /** Richer layout + spacing for the slide-out cart drawer */
  variant?: "page" | "drawer";
}

/** Single line in cart/summary: show crossed-out list price + discounted price when applicable. */
const OrderLinePrices = ({ el }: { el: any }) => {
  if (!hasEffectiveDiscount(el)) {
    return (
      <span className="ac-order-line__price icon-saudi_riyal">{el.lst_price ?? el.price ?? "—"}</span>
    );
  }
  return (
    <span className="ac-order-line__price-stack">
      <span className="ac-order-line__price ac-order-line__price--was icon-saudi_riyal">
        {el.lst_price}
      </span>
      <span className="ac-order-line__price ac-order-line__price--now icon-saudi_riyal">
        {el.lst_price_discount}
      </span>
    </span>
  );
};

const OrderLines = ({ variant = "page" }: OrderLinesProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const { services, products, cartReady, cartLoading, cartError } = useSelector(
    (state: RootState) => state?.global
  );
  const { AddShop } = useSelector((state: RootState) => state.services);
  const isShopLoading = AddShop?.loading === true || cartLoading;

  const isDrawer = variant === "drawer";

  if (!cartReady || cartError) {
    return cartError ? (
      <Alert type="error" showIcon message={t("services.cartSync.failed")}
        action={<Button onClick={() => dispatch(requestCartSync())} loading={cartLoading}>{t("services.cartSync.retry")}</Button>} />
    ) : <div role="status"><Spin size="small" /> {t("services.cartSync.loading")}</div>;
  }

  return (
    <div
      className={`ac-order-lines${isDrawer ? " ac-order-lines--drawer" : ""}`}
    >
      {services.length > 0 ? (
        <section className="ac-order-section" aria-label={t("profile.order.cart.services")}>
          <header className="ac-order-section__header">
            <span className="ac-order-section__title ac--font-expo">
              {t("profile.order.cart.services")}
            </span>
            <span className="ac-order-section__meta">
              <span className="ac-order-section__col-label">
                {t("profile.order.cart.total")}
              </span>
              <span className="ac-order-section__vat">
                ({t("profile.order.cart.vat")})
              </span>
            </span>
          </header>
          <ul className="ac-order-section__list" role="list">
            {services.map((el: any) => (
              <li key={`svc-${el.id}`} className="ac-order-line">
                <button
                  type="button"
                  className="ac-order-line__remove"
                  disabled={isShopLoading}
                  onClick={() => dispatch(toRemoveService(el))}
                  aria-label={`${t("btn.delete")}: ${el.name ?? ""}`}
                >
                  <BiTrash size={isDrawer ? 18 : 14} aria-hidden />
                </button>
                {isDrawer ? (
                  <div className="ac-order-line__thumb-wrap">
                    <ServiceThumbnail
                      className="ac-order-line__thumb"
                      src={
                        isValidServiceImage(el.image_1920)
                          ? el.image_1920
                          : el.image_128
                      }
                      alt=""
                    />
                  </div>
                ) : null}
                <div className="ac-order-line__body">
                  <span className="ac-order-line__name" title={el.name}>
                    {el.name || `#${el.id}`}
                  </span>
                  {formatTintSummary(el).length > 0 ? (
                    <span className="ac-order-line__options">
                      {formatTintSummary(el).join(" · ")}
                    </span>
                  ) : null}
                </div>
                <OrderLinePrices el={el} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {products?.length > 0 ? (
        <section
          className={`ac-order-section${services.length > 0 ? " ac-order-section--spaced" : ""}`}
          aria-label={t("profile.order.cart.products")}
        >
          <header className="ac-order-section__header">
            <span className="ac-order-section__title ac--font-expo">
              {t("profile.order.cart.products")}
            </span>
            <span className="ac-order-section__meta">
              <span className="ac-order-section__col-label">
                {t("profile.order.cart.total")}
              </span>
              <span className="ac-order-section__vat">
                ({t("profile.order.cart.vat")})
              </span>
            </span>
          </header>
          <ul className="ac-order-section__list" role="list">
            {products.map((el: any) => (
              <li key={`prd-${el.id}`} className="ac-order-line ac-order-line--product">
                <button
                  type="button"
                  className="ac-order-line__remove"
                  disabled={isShopLoading}
                  onClick={() => dispatch(toRemoveProduct(el))}
                  aria-label={`${t("btn.delete")}: ${el.name ?? ""}`}
                >
                  <BiTrash size={isDrawer ? 18 : 14} aria-hidden />
                </button>
                {isDrawer ? (
                  <div className="ac-order-line__thumb-wrap">
                    {el.image_1920 || el.image_128 ? (
                      <img className="ac-order-line__thumb" src={el.image_128 || el.image_1920} alt="" loading="lazy" decoding="async" />
                    ) : <RiShoppingBag3Line className="ac-order-line__thumb-fallback" aria-hidden="true" />}
                  </div>
                ) : null}
                <div className="ac-order-line__body">
                  <span className="ac-order-line__name" title={el.name}>
                    {el.name || `#${el.id}`}
                  </span>
                  {formatTintSummary(el).length > 0 ? (
                    <span className="ac-order-line__options">
                      {formatTintSummary(el).join(" · ")}
                    </span>
                  ) : null}
                </div>
                <div className="ac-order-line__qty">
                  <div
                    className={`detail-qty ac-order-line__qty-inner${
                      isDrawer ? " ac-order-line__qty-inner--drawer" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="ac-order-line__qty-btn"
                      disabled={isShopLoading || el.qty <= 1}
                      aria-label={t("profile.order.cart.qtyDecrease")}
                      onClick={() =>
                        !isShopLoading &&
                        el.qty > 1 &&
                        dispatch(
                          toUpdateQuantity({ id: el.id, qty: el.qty - 1 })
                        )
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 22 22"
                        fill="none"
                        aria-hidden
                      >
                        <path
                          d="M15.125 12.375H6.875C6.51033 12.375 6.16059 12.2301 5.90273 11.9723C5.64487 11.7144 5.5 11.3647 5.5 11C5.5 10.6353 5.64487 10.2856 5.90273 10.0277C6.16059 9.76987 6.51033 9.625 6.875 9.625H15.125C15.4897 9.625 15.8394 9.76987 16.0973 10.0277C16.3551 10.2856 16.5 10.6353 16.5 11C16.5 11.3647 16.3551 11.7144 16.0973 11.9723C15.8394 12.2301 15.4897 12.375 15.125 12.375Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                    <span className="ac-order-line__qty-val" aria-live="polite">
                      {el.qty}
                    </span>
                    <button
                      type="button"
                      className="ac-order-line__qty-btn"
                      disabled={isShopLoading}
                      aria-label={t("profile.order.cart.qtyIncrease")}
                      onClick={() =>
                        !isShopLoading &&
                        dispatch(
                          toUpdateQuantity({ id: el.id, qty: el.qty + 1 })
                        )
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 22 22"
                        fill="none"
                        aria-hidden
                      >
                        <path
                          d="M15.5833 10.0833H11.9167V6.41667C11.9167 6.17355 11.8201 5.94039 11.6482 5.76849C11.4763 5.59658 11.2431 5.5 11 5.5C10.7569 5.5 10.5237 5.59658 10.3518 5.76849C10.1799 5.94039 10.0833 6.17355 10.0833 6.41667V10.0833H6.41667C6.17355 10.0833 5.94039 10.1799 5.76849 10.3518C5.59658 10.5237 5.5 10.7569 5.5 11C5.5 11.2431 5.59658 11.4763 5.76849 11.6482C5.94039 11.8201 6.17355 11.9167 6.41667 11.9167H10.0833V15.5833C10.0833 15.8264 10.1799 16.0596 10.3518 16.2315C10.5237 16.4034 10.7569 16.5 11 16.5C11.2431 16.5 11.4763 16.4034 11.6482 16.2315C11.8201 16.0596 11.9167 15.8264 11.9167 15.5833V11.9167H15.5833C15.8264 11.9167 16.0596 11.8201 16.2315 11.6482C16.4034 11.4763 16.5 11.2431 16.5 11C16.5 10.7569 16.4034 10.5237 16.2315 10.3518C16.0596 10.1799 15.8264 10.0833 15.5833 10.0833Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <OrderLinePrices el={el} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
};

export default OrderLines;
